<div class="battery-shell p-2 p-lg-2">
  <div class="battery-panel">
    <div class="battery-header">
      <div class="d-flex align-items-center gap-2">
        <span class="title-icon">
          <lucide-icon [img]="BatteryChargingIcon" [size]="16"></lucide-icon>
        </span>
        <div>
          <h1 class="m-0">Battery Asset Management</h1>
          <p class="page-copy mb-0">Register core BESS asset parameters for capacity, power limits, efficiency, and operational SOC boundaries.</p>
        </div>
      </div>
      <button class="entry-btn" type="button" (click)="openAdd()">
        <lucide-icon [img]="PlusIcon" [size]="14"></lucide-icon>
        <span>ADD ASSET</span>
      </button>
    </div>

    <div class="table-wrap">
      <div class="table-responsive">
        <table class="table mb-0 battery-table">
          <thead>
            <tr>
              <th>S. No.</th>
              <th class="d-none">ID</th>
              <th>Plant Type</th>
              <th>Power Capacity(MW)</th>
              <th>Energy Capacity(MW)</th>
              <th>Charging Power(MW) <br />(Per Block)</th>
              <th>Discharging Power(MW) <br />(Per Block)</th>
              <th>SOC Initial(%)</th>
              <th>SOC Final(%)</th>
              <th>Charging Efficiency(%) <br />(Per Block)</th>
              <th>Discharging Efficiency(%) <br />(Per Block)</th>
              <th>Cycle (Per Day)</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            @if (loading()) {
              <tr>
                <td colspan="13" class="text-center py-5 text-muted">Loading battery entries...</td>
              </tr>
            } @else if (sortedEntries().length === 0) {
              <tr>
                <td colspan="13" class="text-center py-5 text-muted">No battery entries found.</td>
              </tr>
            }
            @for (entry of sortedEntries(); track entry.id; let i = $index) {
              <tr>
                <td>{{ i + 1 }}</td>
                <td class="d-none">{{ entry.entry_id }}</td>
                <td>{{ entry.plant_type }}</td>
                <td>{{ entry.power_capacity_mw }}</td>
                <td>{{ entry.energy_capacity_mw }}</td>
                <td>{{ entry.charging_power_mw }}</td>
                <td>{{ entry.discharging_power_mw }}</td>
                <td>{{ entry.soc_initial }}</td>
                <td>{{ entry.soc_final }}</td>
                <td>{{ entry.charging_efficiency }}</td>
                <td>{{ entry.discharging_efficiency }}</td>
                <td>{{ entry.cycle_per_day }}</td>
                <td>
                  <div class="action-list">
                    <button class="action-btn edit" type="button" title="Edit" (click)="openEdit(entry)">
                      <lucide-icon [img]="PencilIcon" [size]="14"></lucide-icon>
                    </button>
                    <button class="action-btn delete" type="button" title="Delete" (click)="remove(entry)">
                      <lucide-icon [img]="Trash2Icon" [size]="14"></lucide-icon>
                    </button>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  </div>
</div>

@if (showForm()) {
  <div class="battery-modal-backdrop">
    <div class="battery-modal">
      <div class="battery-modal-body">
        <button type="button" class="close-btn" aria-label="Close" (click)="closeForm()">
          <lucide-icon [img]="XIcon" [size]="16"></lucide-icon>
        </button>

        <h2>{{ editingId() ? 'Edit Battery Asset' : 'Add Battery Asset' }}</h2>

        <div class="form-grid">
          <div class="field-wrap field-wrap-full vendor-field">
            <label>Vendor</label>
            <div class="vendor-search-wrap">
              <input
                class="form-control"
                placeholder="Search vendor by name, NOAR ID, type, or state"
                [value]="form().vendorSearch"
                (focus)="openVendorSearch()"
                (input)="updateVendorSearch(($any($event.target).value || '').toString())"
              />
              @if (form().vendorId) {
                <button class="vendor-clear-btn" type="button" aria-label="Clear vendor" (click)="clearVendor()">
                  <lucide-icon [img]="XIcon" [size]="14"></lucide-icon>
                </button>
              }
            </div>
            @if (vendorSearchOpen()) {
              <div class="vendor-search-panel">
                @if (vendorsLoading()) {
                  <div class="vendor-option muted">Loading vendors...</div>
                } @else if (filteredVendors().length === 0) {
                  <div class="vendor-option muted">No vendors match your search.</div>
                } @else {
                  @for (vendor of filteredVendors(); track vendor.id) {
                    <button class="vendor-option" type="button" (click)="selectVendor(vendor)">
                      <span>{{ vendor.vendor_name }}</span>
                      <small>{{ vendor.vendor_type }}{{ vendor.noar_id ? ' • ' + vendor.noar_id : '' }}</small>
                    </button>
                  }
                }
              </div>
            }
          </div>
          <div class="field-wrap">
            <label>Plant Type</label>
            <input class="form-control" placeholder="Select a vendor to populate plant type" [value]="form().plantType" readonly />
          </div>
          <div class="field-wrap">
            <label>Power Capacity (MW)</label>
            <input class="form-control" placeholder="e.g. 120" [value]="form().powerCapacityMw" (input)="setField('powerCapacityMw', ($any($event.target).value || '').toString())" />
          </div>
          <div class="field-wrap">
            <label>Energy Capacity (MW)</label>
            <input class="form-control" placeholder="e.g. 500" [value]="form().energyCapacityMw" (input)="setField('energyCapacityMw', ($any($event.target).value || '').toString())" />
          </div>
          <div class="field-wrap">
            <label>Charging Power (MW)</label>
            <input class="form-control" placeholder="e.g. 150" [value]="form().chargingPowerMw" (input)="setField('chargingPowerMw', ($any($event.target).value || '').toString())" />
          </div>
          <div class="field-wrap">
            <label>Discharging Power (MW)</label>
            <input class="form-control" placeholder="e.g. 140" [value]="form().dischargingPowerMw" (input)="setField('dischargingPowerMw', ($any($event.target).value || '').toString())" />
          </div>
          <div class="field-wrap">
            <label>SOC Initial (%)</label>
            <input class="form-control" placeholder="e.g. 30" [value]="form().socInitial" (input)="setField('socInitial', ($any($event.target).value || '').toString())" />
          </div>
          <div class="field-wrap">
            <label>SOC Final (%)</label>
            <input class="form-control" placeholder="e.g. 20" [value]="form().socFinal" (input)="setField('socFinal', ($any($event.target).value || '').toString())" />
          </div>
          <div class="field-wrap">
            <label>Charging Efficiency</label>
            <input class="form-control" placeholder="e.g. 0.95" [value]="form().chargingEfficiency" (input)="setField('chargingEfficiency', ($any($event.target).value || '').toString())" />
          </div>
          <div class="field-wrap">
            <label>Discharging Efficiency</label>
            <input class="form-control" placeholder="e.g. 0.95" [value]="form().dischargingEfficiency" (input)="setField('dischargingEfficiency', ($any($event.target).value || '').toString())" />
          </div>
          <div class="field-wrap">
            <label>Cycle (Per Day)</label>
            <input class="form-control" placeholder="e.g. 2" [value]="form().cyclePerDay" (input)="setField('cyclePerDay', ($any($event.target).value || '').toString())" />
          </div>
        </div>

        @if (formError()) {
          <div class="text-danger small mt-3">{{ formError() }}</div>
        }

        <div class="modal-actions">
          <button class="submit-btn" type="button" [disabled]="submitting()" (click)="save()">{{ submitting() ? 'Saving...' : editingId() ? 'Update' : 'Add' }}</button>
          <button class="cancel-btn" type="button" (click)="closeForm()">Cancel</button>
        </div>
      </div>
    </div>
  </div>
}
